import React, { useMemo, useState } from 'react';

const Icn = {
    search: (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    x: (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    check: (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 6.2l2.3 2.3L9.5 3.5"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
};

const initialsOf = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const avatarPaletteOf = (id) => {
    const s = String(id || '');
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return `av-${(h % 6) + 1}`;
};

const fullName = (u) => {
    if (!u) return '';
    const n = `${u.firstName || ''} ${u.lastName || ''}`.trim();
    return n || u.email || 'User';
};

// Normalize a linguist record from /api/team into a flat shape this popover uses.
// The endpoint returns { userId: {…populated User…}, translatedLanguages, availability, workloadCapacity, … }
const normaliseLinguist = (raw) => {
    const u = raw.userId || raw;
    if (!u || !(u._id || u.id)) return null;
    const langs = (raw.translatedLanguages || u.translatedLanguages || [])
        .map((l) => (typeof l === 'string' ? l : (l.code || l.lang || l.label || '')))
        .filter(Boolean)
        .map((s) => s.toUpperCase().slice(0, 2));
    return {
        id: String(u._id || u.id),
        name: fullName(u),
        ini: initialsOf(fullName(u)),
        av: avatarPaletteOf(u._id || u.id),
        langs,
        load: raw.assignedProjectsCount ?? raw.workloadCapacity ?? 0,
        avail: (raw.availability || u.availability || 'available').toLowerCase() === 'busy' ? 'busy' : 'available',
    };
};

const AssignPopover = ({
    lang,
    assignment,
    linguists,
    currentUserId,
    onAssign,
    onClose,
}) => {
    const [role, setRole] = useState('translator');
    const [q, setQ] = useState('');
    const [showAll, setShowAll] = useState(false);

    const langCode = (lang.code || '').toUpperCase().slice(0, 2);
    const langPair = lang.pair || `${(lang.sourceCode || 'EN').toUpperCase()} → ${langCode}`;
    const selectedId = assignment[role] ? String(assignment[role]) : null;

    const pool = useMemo(() => {
        const items = (linguists || []).map(normaliseLinguist).filter(Boolean);
        return items
            .map((l) => ({ ...l, you: currentUserId && String(currentUserId) === l.id }))
            .filter((l) => (showAll || l.langs.includes(langCode))
                && (!q || l.name.toLowerCase().includes(q.toLowerCase())));
    }, [linguists, showAll, q, langCode, currentUserId]);

    return (
        <>
            <div className="assign-overlay" onClick={onClose} />
            <div className="assign-pop" onClick={(e) => e.stopPropagation()}>
                <div className="ap-h">
                    <div className="ap-title">Assign for <b>{langPair}</b></div>
                    <button type="button" className="ap-x" onClick={onClose} aria-label="Close">{Icn.x}</button>
                </div>
                <div className="ap-roles">
                    {['translator', 'reviewer'].map((r) => (
                        <button
                            type="button"
                            key={r}
                            className={role === r ? 'on' : ''}
                            onClick={() => setRole(r)}
                        >
                            {r === 'translator' ? 'Translator' : 'Reviewer'}
                            {assignment[r] && <span className="ap-role-dot" />}
                        </button>
                    ))}
                </div>
                <div className="ap-search">
                    {Icn.search}
                    <input
                        autoFocus
                        placeholder="Search linguists…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                </div>
                <div className="ap-list">
                    {pool.map((l) => {
                        const sel = l.id === selectedId;
                        const fits = l.langs.includes(langCode);
                        return (
                            <button
                                type="button"
                                key={l.id}
                                className={`ap-opt ${sel ? 'sel' : ''}`}
                                onClick={() => onAssign(role, sel ? null : l.id)}
                            >
                                <span className={`av-s ${l.av}`}>{l.ini}</span>
                                <span className="ap-meta">
                                    <b>{l.name}{l.you ? ' (you)' : ''}</b>
                                    <span>
                                        {(l.langs.length ? l.langs.join(' · ') : 'No languages set')}
                                        {' · '}{l.load === 0 ? 'free' : `${l.load} active`}
                                        {!fits && <span className="ap-outside"> · outside specialty</span>}
                                    </span>
                                </span>
                                <span className={`ap-avail ${l.avail}`} title={l.avail} />
                                {sel && <span className="ap-check">{Icn.check}</span>}
                            </button>
                        );
                    })}
                    {pool.length === 0 && (
                        <div className="ap-empty">
                            No linguists{!showAll ? ` cover ${langCode}` : ''}{q ? ` match "${q}"` : ''}.
                        </div>
                    )}
                </div>
                <div className="ap-foot">
                    <label className="ap-allchk">
                        <input
                            type="checkbox"
                            checked={showAll}
                            onChange={(e) => setShowAll(e.target.checked)}
                        />
                        Show everyone
                    </label>
                    {selectedId && (
                        <button
                            type="button"
                            className="ap-unassign"
                            onClick={() => onAssign(role, null)}
                        >
                            Unassign {role}
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};

export default AssignPopover;
